import React, { useEffect, useRef, useState } from "react";
import "../styles/Service.css";
import AnimatedTitle from "../components/AnimatedTitle";
import AnimatedText from "./AnimatedText";

import AOS from "aos";
import "aos/dist/aos.css";

import serviceLogo from "../assets/van-logo.png";
import serviceLogo1 from "../assets/logo.png";

import tempoIcon from "../assets/tt27.jpeg";
import hillIcon from "../assets/tt4.jpg";
import safeIcon from "../assets/badge.png";
import priceIcon from "../assets/price.avif";

const services = [
  {
    id: "01",
    icon: tempoIcon,
    title: "18-Seater Tempo Traveller",
    description:
      "Luxury AC 18-seater Tempo Traveller in Mayiladuthurai with spacious seating, comfortable interiors, and smooth travel experience for family and group trips."
  },
  {
    id: "02",
    icon: hillIcon,
    title: "Hill & Group Trips",
    description:
      "Best service for hill station trips, group travel, and outstation tours with well-maintained vehicles and professional driver."
  },
  {
    id: "03",
    icon: safeIcon,
    title: "Safe & Secure Journey",
    description:
      "Highly experienced driver, 25+ years in transport service, ensuring safe, reliable, and stress-free travel for all passengers."
  },
  {
    id: "04",
    icon: priceIcon,
    title: "Affordable & Best Pricing",
    description:
      "Best Tempo Traveller rental price in Mayiladuthurai with no hidden charges, timely service, and customer-friendly support."
  }
];

const Service = () => {
  const cardsRef = useRef([]);
  const marqueeRef = useRef(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

  /* ✅ ===== SIMPLE SEO (NO PACKAGE REQUIRED) ===== */
  useEffect(() => {
    document.title =
      "Services | Best 18 Seater Tempo Traveller in Mayiladuthurai | Maha Cabs";

    const setMeta = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    setMeta(
      "description",
      "Maha Cabs provides premium 18-seater AC Tempo Traveller in Mayiladuthurai for family trips, hill trips, corporate travel, weddings, and all India tours with experienced driver and affordable pricing."
    );

    setMeta(
      "keywords",
      "tempo traveller services in mayiladuthurai, 18 seater tempo traveller mayiladuthurai, tempo traveller hire mayiladuthurai, hill trips tempo traveller, group travel mayiladuthurai, affordable tempo traveller mayiladuthurai, luxury tempo traveller mayiladuthurai, maha cabs services"
    );
  }, []);


  /* Detect screen size */
  useEffect(() => {
    const checkScreen = () => {
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  /* Initialize AOS */
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 120,
      easing: "ease-out-cubic",
    });
  }, []);

  /* Intersection observer for cards */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.2 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  /* ===== FIXED MARQUEE ANIMATION (NO CRASH) ===== */
  useEffect(() => {
    if (!isMobileOrTablet || !marqueeRef.current) return;

    let position = 0;
    let direction = 1;
    const speed = 0.4;
    let animationId;

    const animate = () => {
      const track = marqueeRef.current;

      if (!track) return;

      position += speed * direction;

      if (position > 0) direction = -1;
      if (position < -30) direction = 1;

      track.style.transform = `translateX(${position}%)`;

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isMobileOrTablet]);

  return (
    <section className="service-section" id="services">

      {/* Header */}
      <div className="service-header" data-aos="fade-up">
        <img src={serviceLogo} alt="Service Logo" className="service-logo" />
        <span className="service-tag">SERVICES</span>
      </div>

      {/* Animated Title */}
      <AnimatedTitle
        text="SERVICES WE’RE PROVIDING TO CUSTOMERS"
        className="service-title"
      />

      {/* Description */}
      <p className="service-subtext" data-aos="fade-up" data-aos-delay="100">
        We are the Best Tempo Traveller rental service in Mayiladuthurai,
        Offering a premium 18-seater AC Tempo Traveller for family trips,
        group tours, corporate travel, wedding travel, and hill station
        journeys. Our vehicle is designed with luxury seating, modern
        interiors, and smooth suspension to provide a comfortable and
        relaxing travel experience on both short and long journeys.
      </p>

      <p className="service-subtext" data-aos="fade-up" data-aos-delay="200">
        With over 25 years of experience in transportation, we prioritize
        passenger safety, punctuality, and customer satisfaction. Our
        professional and well-trained driver ensure a safe, secure, and
        hassle-free journey. Whether it is a hill trip, temple tour, beach
        trip, or outstation group travel, Mahacabs guarantees reliable
        service, affordable pricing, and the best travel experience.
      </p>

      {/* Service Cards */}
      <div className="service-container">
        {services.map((item, index) => (
          <div
            ref={(el) => (cardsRef.current[index] = el)}
            className={`service-card delay-${index}`}
            key={item.id}
            data-aos="fade-up"
            data-aos-delay={200 + index * 250}
            data-aos-anchor-placement="top-bottom"
          >
            <div className="icon-box">
              <img src={item.icon} alt={item.title} />
            </div>

            <AnimatedText text={item.title} Tag="h3" />

            <p>{item.description}</p>

            <span className="service-number">{item.id}</span>
          </div>
        ))}
      </div>

      {/* Infinite Text Slider */}
      <div className="service-marquee" data-aos="fade-up">
        <div className="marquee-track" ref={marqueeRef}>
          {[
            "Premium",
            "Traveller",
            "Trips",
            "18Seater",
            "Affordable",
            "Comfortable",
            "Premium",
            "Traveller",
            "18Seater",
            "Trips",
            "Affordable",
            "Comfortable",
          ].map((text, index) => (
            <div className="marquee-item" key={index}>
              <img src={serviceLogo1} alt="logo" />
              <span>{text}</span>
            </div>
          ))}

          {/* Duplicate items */}
          {[
            "Premium",
            "Traveller",
            "Trips",
            "18Seater",
            "Affordable",
            "Comfortable",
            "Premium",
            "Traveller",
            "Trips",
            "18Seater",
            "Affordable",
            "Comfortable",
          ].map((text, index) => (
            <div className="marquee-item" key={"dup-" + index}>
              <img src={serviceLogo1} alt="logo" />
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>

    </section>
  );
};

export default Service;
